import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getSalesConfirmationLinesApi } from "api/salesConfirmation";
import { SalesConfirmation, SalesConfirmationLine } from "interface/sales";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { FieldFull, FieldSplit, FieldRow } from "ui/CardField";
import { formatDate, formatAmount, formatWeight } from "utils/formatters";

interface SalesConfirmationMobileListProps {
	items: SalesConfirmation[];
	loading: boolean;
	onConfirm: (item: SalesConfirmation) => Promise<void>;
	onOpenPdf: (item: SalesConfirmation) => void;
	onLoadMore: () => void;
}

const getStatusClassName = (status?: number | null) => {
	if (status === 3) return "sales-confirmation-list-card--status-green";
	if (status === 2) return "sales-confirmation-list-card--status-yellow";
	return "sales-confirmation-list-card--status-red";
};

const formatVessel = (item: SalesConfirmation) => {
	const registration = item.vesselRegistrationId ?.trim();
	const vesselName = item.vesselName?.trim();

	if (registration && vesselName) {
		return `${registration} - ${vesselName}`;
	}

	return registration || vesselName || "-";
};


const SalesConfirmationMobileList: FC<SalesConfirmationMobileListProps> = ({
	items,
	loading,
	onConfirm,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const [expandedSales, setExpandedSales] = useState<number[]>([]);
	const [lineMap, setLineMap] = useState<Record<number, SalesConfirmationLine[]>>(
		{},
	);
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<number, boolean>>(
		{},
	);
	const [confirmingIds, setConfirmingIds] = useState<number[]>([]);

	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	const toggleLines = (item: SalesConfirmation) => {
		if (expandedSales.includes(item.id)) {
			setExpandedSales((prev) => prev.filter((id) => id !== item.id));
			return;
		}

		setExpandedSales((prev) => [...prev, item.id]);
		if (lineMap[item.id] || lineLoadingMap[item.id]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.id]: true }));

		getSalesConfirmationLinesApi({
			salesId: item.id,
			confirmationTypeId: item.salesConfirmationTypeId,
		})
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.id]: (response?.data || []) as SalesConfirmationLine[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({ ...prev, [item.id]: false }));
			});
	};

	const handleConfirm = (item: SalesConfirmation) => {
		if (confirmingIds.includes(item.id) || (item.status !== 1 && item.status !== 2)) {
			return;
		}

		setConfirmingIds((prev) => [...prev, item.id]);
		onConfirm(item).finally(() => {
			setConfirmingIds((prev) => prev.filter((id) => id !== item.id));
		});
	};

	return (
		<div className="sales-confirmation-list-mobile" onScroll={handleScroll}>
			{empty && (
				<div className="sales-confirmation-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const expanded = expandedSales.includes(item.id);
				const lines = lineMap[item.id] || [];
				const linesLoading = lineLoadingMap[item.id] === true;
				const confirming = confirmingIds.includes(item.id);
				const canConfirm = item.status === 1 || item.status === 2;
				const itemKey = `${item.id}-${item.salesConfirmationTypeId}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`sales-confirmation-list-card ${getStatusClassName(item.status)}`}
					>
						<div className="sales-confirmation-list-card-header">
							<div className="sales-confirmation-list-card-note">{item.id}</div>
							<div className="sales-confirmation-list-card-date">
								<GrideDateFormatter
									date={item.salesTime}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="sales-confirmation-list-card-body">
							<FieldRow
								items={[
									{ label: t("Buyer"), value: item.buyerName || "-" },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Vessel"), value: formatVessel(item) },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("Type"), value: item.salesType || "-" },
									{ label: t("RegistrationDateShort"), value: formatDate(item.registrationTime) },
								]}
							/>
							<FieldRow
								items={[
									{ label: t("TotalWeight"), value: formatWeight(item.netLiveWeightKg) },
									{ label: t("Value"), value: formatAmount(item.buyerAmount) },
								]}
							/>
							<FieldRow
								items={[
									{ label: "Tekst", value: item.confirmationText || "-" },
								]}
							/>
						</div>

						<div className="sales-confirmation-list-card-actions">
							{canConfirm && (
								<button
									type="button"
									className="sales-confirmation-list-icon-btn"
									onClick={() => handleConfirm(item)}
									disabled={confirming}
									title={t("Confirm")}
								>
									<img
										src={`/icon/${ICON_NAMES.Confirmation}.svg`}
										alt={t("Confirm")}
									/>
								</button>
							)}
							<button
								type="button"
								className="sales-confirmation-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
							<button
								type="button"
								className="sales-confirmation-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="sales-confirmation-list-lines">
								{linesLoading && (
									<div className="sales-confirmation-list-lines-loading">
										{t("Loading")}
									</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="sales-confirmation-list-lines-empty">
										{t("NoRows")}
									</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.id ?? "noid"}-${line.lineNumber ?? "noline"}-${lineIndex}`}
											className="sales-confirmation-list-line-row"
										>
											<div className="sales-confirmation-list-line-number">
												#{line.lineNumber}
											</div>
											<div className="sales-confirmation-list-line-product">
												<div>{line.product || "-"}</div>
												<div className="sales-confirmation-list-line-meta">
													{t("Pieces")}: {line.numberOfPieces ?? 0}
												</div>
											</div>
											<div className="sales-confirmation-list-line-summary">
												<strong>{formatWeight(line.netLiveWeightKg)}</strong>
												<span>{formatAmount(line.amount ?? line.price)}</span>
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && (
				<div className="sales-confirmation-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default SalesConfirmationMobileList;